import { nullishToUndefinedable } from '@repo/utils/valibot';
import * as v from 'valibot';
import { EntityPlacementCtaTypeEnum, MarketplaceStatusEnum } from './enums';
import {
  CardUserSchema,
  CobrandAirlineSchema,
  CobrandHotelSchema,
  PerkSchema,
  RewardSchema,
  SignUpBonusTypeSchema,
} from './fields';

export const driverSchema = v.object({
  label: v.string('driver.label is required'),
  value: nullishToUndefinedable(v.string('driver.value must be a string when provided')), //TODO: why is this a thing?
  description: nullishToUndefinedable(
    v.string('driver.description must be a string when provided'),
  ),
  labelTooltip: nullishToUndefinedable(
    v.string('driver.labelTooltip must be a string when provided'),
  ),
  valueTooltip: nullishToUndefinedable(
    v.string('driver.valueTooltip must be a string when provided'),
  ),
  valueTooltipHasHtml: v.nullish(
    v.boolean('driver.valueTooltipHasHtml must be a boolean when provided'),
    false,
  ),
});

export const analyticsPropsSchema = v.object({
  hasLegacyImpression: v.boolean('analyticsProps.hasLegacyImpression is required'),
  institutionId: v.string('analyticsProps.institutionId is required'),
  legacyAdditionalEventData: nullishToUndefinedable(
    v.string('analyticsProps.legacyAdditionalEventData must be a string when provided'),
  ),
  legacyIssuer: v.string('analyticsProps.legacyIssuer is required'),
  legacySlug: nullishToUndefinedable(
    v.string('analyticsProps.legacySlug must be a string when provided'),
  ),
  legacyVertical: nullishToUndefinedable(
    v.string('analyticsProps.legacyVertical must be a string when provided'),
  ),
  offerId: v.string('analyticsProps.offerId is required'),
  productId: v.string('analyticsProps.productId is required'),
});

export const baseOfferImageSchema = v.object({
  height: v.number('baseOfferImage.height is required'),
  resizableImagePath: nullishToUndefinedable(
    v.string('baseOfferImage.resizableImagePath must be a string when provided'),
  ),
  source: v.pipe(
    v.string('baseOfferImage.source is required'),
    v.trim(),
    v.url('baseOfferImage.source must be a valid URL'),
  ),
  sourceLarge: v.pipe(
    v.string('baseOfferImage.sourceLarge is required'),
    v.trim(),
    v.url('baseOfferImage.sourceLarge must be a valid URL'),
  ),
  sourceMedium: v.pipe(
    v.string('baseOfferImage.sourceMedium is required'),
    v.trim(),
    v.url('baseOfferImage.sourceMedium must be a valid URL'),
  ),
  width: v.number('baseOfferImage.width is required'),
});

export const bestOfAwardsSchema = v.object({
  category: v.string('bestOfAwards.category is required'),
  year: v.number('bestOfAwards.year is required'),
});

const ctaSchema = v.object({
  link: v.string('cta.link is required'),
  showIcon: v.boolean('cta.showIcon is required'),
  subtext: nullishToUndefinedable(v.string('cta.subtext must be a string when provided')),
  text: v.pipe(
    v.string('cta.text is required'),
    v.minLength(1, 'cta.text must have at least 1 character'),
  ),
  type: v.enum(
    EntityPlacementCtaTypeEnum,
    'cta.type must be a valid EntityPlacementCtaTypeEnum value',
  ),
});

export const rewardsRatesSchema = v.object({
  description: v.string('rewardsRates.description is required'),
  rate: v.number('rewardsRates.rate is required'),
});

export const BaseOfferSchema = v.object({
  analyticsProps: analyticsPropsSchema,
  annualFee: v.pipe(v.number('annualFee is required in marketplaceEntity'), v.minValue(0)),
  annualFeeDetails: nullishToUndefinedable(
    v.string('annualFeeDetails must be a string when provided in marketplaceEntity'),
  ),
  applyLink: v.pipe(
    v.string('applyLink is required in marketplaceEntity'),
    v.trim(),
    v.url('applyLink must be a valid URL'),
  ),
  aprDisplayList: nullishToUndefinedable(v.array(v.string('aprDisplayList item must be a string'))),
  aprIntroMessageList: nullishToUndefinedable(
    v.array(v.string('aprIntroMessageList item must be a string')),
  ),
  approvalLikelihood: nullishToUndefinedable(
    v.number('approvalLikelihood must be a number when provided in marketplaceEntity'),
  ),
  aprMax: v.nullable(v.pipe(v.number(), v.minValue(0))),
  aprMin: v.nullable(v.pipe(v.number(), v.minValue(0))),
  balanceTransferFee: v.nullable(v.pipe(v.number(), v.minValue(0))),
  balanceTransferIntroApr: v.nullable(v.pipe(v.number(), v.minValue(0))),
  balanceTransferIntroTime: v.nullable(v.pipe(v.number(), v.minValue(0))),
  bestOfAwards: v.array(bestOfAwardsSchema, 'bestOfAwards must be an array of best of awards'),
  billingCycleUnits: v.string('billingCycleUnits is required in marketplaceEntity'),
  cardUser: v.nullish(CardUserSchema),
  cobrandAirline: v.nullish(CobrandAirlineSchema),
  cobrandHotel: v.nullish(CobrandHotelSchema),
  consBullets: v.fallback(
    v.array(
      v.string('offer.consBullets[number] must be a string'),
      'offer.consBullets must be an array of strings',
    ),
    [],
  ),
  cta: ctaSchema,
  displayName: v.string('displayName is required in marketplaceEntity'),
  driverAnnualFee: v.object(
    {
      label: v.string('label is required in driverAnnualFee'),
      value: nullishToUndefinedable(
        v.string('value must be a string when provided in driverAnnualFee'),
      ),
    },
    'driverAnnualFee must be a valid object',
  ),
  driverBalanceTransferFeeDescription: v.object(
    {
      description: nullishToUndefinedable(
        v.string(
          'description must be a string when provided in driverBalanceTransferFeeDescription',
        ),
      ),
      label: v.string('label is required in driverBalanceTransferFeeDescription'),
      value: nullishToUndefinedable(
        v.string('value must be a string when provided in driverBalanceTransferFeeDescription'),
      ),
      valueTooltip: nullishToUndefinedable(
        v.string(
          'valueTooltip must be a string when provided in driverBalanceTransferFeeDescription',
        ),
      ),
    },
    'driverBalanceTransferFeeDescription must be a valid object',
  ),
  driverBonus: nullishToUndefinedable(
    v.object(
      {
        description: nullishToUndefinedable(
          v.string('description must be a string when provided in driverBonus'),
        ),
        label: v.string('label is required in driverBonus'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverBonus'),
        ),
        valueTooltip: nullishToUndefinedable(
          v.string('valueTooltip must be a string when provided in driverBonus'),
        ),
      },
      'driverBonus must be a valid object',
    ),
  ),
  driverForeignTransactionFee: nullishToUndefinedable(
    v.object(
      {
        description: nullishToUndefinedable(
          v.string('description must be a string when provided in driverForeignTransactionFee'),
        ),
        label: v.string('label is required in driverForeignTransactionFee'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverForeignTransactionFee'),
        ),
        valueTooltip: nullishToUndefinedable(
          v.string('valueTooltip must be a string when provided in driverForeignTransactionFee'),
        ),
      },
      'driverForeignTransactionFee must be a valid object',
    ),
  ),
  driverIntroApr: nullishToUndefinedable(
    v.object(
      {
        label: v.string('label is required in driverIntroApr'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverIntroApr'),
        ),
      },
      'driverIntroApr must be a valid object',
    ),
  ),
  driverIntroOffer: v.object(
    {
      description: nullishToUndefinedable(
        v.string('description must be a string when provided in driverIntroOffer'),
      ),
      label: v.string('label is required in driverIntroOffer'),
      value: nullishToUndefinedable(
        v.string('value must be a string when provided in driverIntroOffer'),
      ),
      valueTooltip: nullishToUndefinedable(
        v.string('valueTooltip must be a string when provided in driverIntroOffer'),
      ),
    },
    'driverIntroOffer must be a valid object',
  ),
  driverMinimumDeposit: v.object(
    {
      description: nullishToUndefinedable(
        v.string('description must be a string when provided in driverMinimumDeposit'),
      ),
      label: v.string('label is required in driverMinimumDeposit'),
      value: nullishToUndefinedable(
        v.string('value must be a string when provided in driverMinimumDeposit'),
      ),
    },
    'driverMinimumDeposit must be a valid object',
  ),
  driverRegularApr: nullishToUndefinedable(
    v.object(
      {
        label: v.string('label is required in driverRegularApr'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverRegularApr'),
        ),
        valueTooltip: nullishToUndefinedable(
          v.string('valueTooltip must be a string when provided in driverRegularApr'),
        ),
      },
      'driverRegularApr must be a valid object',
    ),
  ),
  driverRewardRate: nullishToUndefinedable(
    v.object(
      {
        description: nullishToUndefinedable(
          v.string('description must be a string when provided in driverRewardRate'),
        ),
        label: v.string('label is required in driverRewardRate'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverRewardRate'),
        ),
        valueTooltip: nullishToUndefinedable(
          v.string('valueTooltip must be a string when provided in driverRewardRate'),
        ),
      },
      'driverRewardRate must be a valid object',
    ),
  ),
  eligibleForNerdWalletPlusBonus: nullishToUndefinedable(
    v.boolean(
      'eligibleForNerdWalletPlusBonus must be a boolean when provided in marketplaceEntity',
    ),
  ),
  entityType: nullishToUndefinedable(
    v.string('entityType must be a string when provided in marketplaceEntity'),
  ),
  foreignTransactionFee: v.nullable(v.pipe(v.number(), v.minValue(0))),
  id: v.string('id is required in marketplaceEntity'),
  image: baseOfferImageSchema,
  institution: v.object(
    {
      id: v.string('id is required in institution'),
      name: v.string('name is required in institution'),
    },
    'institution must be a valid object',
  ),
  introBalanceTransfers: nullishToUndefinedable(
    v.string('introBalanceTransfers must be a string when provided in marketplaceEntity'),
  ),
  introPurchases: nullishToUndefinedable(
    v.string('introPurchases must be a string when provided in marketplaceEntity'),
  ),
  legacyId: v.number('legacyId is required in marketplaceEntity'),
  limitedCreditHistory: v.boolean('limitedCreditHistory is required in marketplaceEntity'),
  marketingBullets: v.array(
    v.string('marketingBullet must be a string'),
    'marketingBullets must be an array of strings',
  ),
  maxFico: v.pipe(
    v.number('maxFico is required in marketplaceEntity'),
    v.minValue(300),
    v.maxValue(850),
  ),
  minFico: v.pipe(
    v.number('minFico is required in marketplaceEntity'),
    v.minValue(300),
    v.maxValue(850),
  ),
  minimumDeposit: v.nullable(v.pipe(v.number(), v.minValue(0))),
  monetizable: v.picklist(
    ['yes_assumed', 'no', 'no_because_of_limit'],
    'monetizable must be one of: yes_assumed, no, no_because_of_limit',
  ),
  name: v.string('name is required in marketplaceEntity'),
  ongoingApr: nullishToUndefinedable(
    v.string('ongoingApr must be a string when provided in marketplaceEntity'),
  ),
  perks: v.nullable(v.array(PerkSchema)),
  phoneNumber: nullishToUndefinedable(
    v.string('phoneNumber must be a string when provided in marketplaceEntity'),
  ),
  product: v.object(
    {
      id: v.string('id is required in product'),
      name: v.string('name is required in product'),
    },
    'product must be a valid object',
  ),
  productType: v.string('productType is required in marketplaceEntity'),
  prosBullets: v.array(
    v.string('prosBullet must be a string'),
    'prosBullets must be an array of strings',
  ),
  purchaseIntroApr: v.nullable(v.pipe(v.number(), v.minValue(0))),
  purchaseIntroTime: v.nullable(v.pipe(v.number(), v.minValue(0))),
  reviewLink: v.pipe(
    v.string('reviewLink is required in marketplaceEntity'),
    v.trim(),
    v.url('reviewLink must be a valid URL'),
  ),
  rewardRate: nullishToUndefinedable(
    v.number('rewardRate must be a number when provided in marketplaceEntity'),
  ),
  rewardRateDetails: nullishToUndefinedable(
    v.string('rewardRateDetails must be a string when provided in marketplaceEntity'),
  ),
  rewardRateNotes: nullishToUndefinedable(
    v.string('rewardRateNotes must be a string when provided in marketplaceEntity'),
  ),
  rewardType: v.pipe(
    v.nullable(v.string('rewardType must be a string when provided'), 'no rewards'),
    v.transform(val => val?.toLowerCase()),
    v.picklist(
      ['no rewards', 'points', 'cash', 'cash back', 'miles'],
      'rewardType must be one of: no rewards, points, cash, cash back, miles',
    ),
  ),
  rewards: v.nullable(v.array(RewardSchema)),
  rewardsRates: v.array(rewardsRatesSchema, 'rewardsRates must be an array of reward rates'),
  showNameInComparisonTable: nullishToUndefinedable(
    v.boolean('showNameInComparisonTable must be a boolean when provided in marketplaceEntity'),
  ),
  signUpBonus: v.pipe(
    v.number('signUpBonus is required in marketplaceEntity'),
    v.minValue(0),
    v.integer(),
  ),
  signUpBonusDetails: v.string('signUpBonusDetails is required in marketplaceEntity'),
  signUpBonusDetailsAnnotation: nullishToUndefinedable(
    v.string('signUpBonusDetailsAnnotation must be a string when provided in marketplaceEntity'),
  ),
  signUpBonusType: SignUpBonusTypeSchema,
  starRating: nullishToUndefinedable(v.pipe(v.number(), v.minValue(0), v.maxValue(5))),
  starRatingTooltip: nullishToUndefinedable(
    v.string('starRatingTooltip must be a string when provided in marketplaceEntity'),
  ),
  status: v.pipe(
    v.enum(MarketplaceStatusEnum, 'status must be a valid MarketplaceStatusEnum value'),
    v.transform(val => val?.toLowerCase()),
  ),
  termsAndFeesLink: nullishToUndefinedable(
    v.string('termsAndFeesLink must be a string when provided in marketplaceEntity'),
  ),
  versionId: v.number('versionId is required in marketplaceEntity'),
});
